import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { setCanonical, setPageTitle, setMetaDescription } from "@/utils/seo";
import { SidebarAds, BottomAdBanner } from "@/components/SponsorBanners";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCanonical("/blog");
    setPageTitle("Blog - Financial Tips & Cost of Living Insights");
    setMetaDescription("Expert articles on cost of living, salary affordability, tax planning, and financial tips for US cities.");
    axios.get(`${API}/blogs`).then(r => setBlogs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      <section className="hero-pattern py-12 md:py-16">
        <div className="container-main">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3">Blog</h1>
          <p className="text-base text-slate-500 max-w-xl">Financial insights, cost of living guides, and salary tips for US cities.</p>
        </div>
      </section>

      <div className="container-main py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8">
          <div>
            {loading ? (
              <p className="text-center text-slate-400">Loading articles...</p>
            ) : blogs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No articles published yet. Check back soon!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {blogs.map(blog => (
                  <Link key={blog.slug} to={`/blog/${blog.slug}`} className="no-underline" data-testid={`blog-card-${blog.slug}`}>
                    <Card className="card-hover h-full">
                      <CardContent className="p-6">
                        <h2 className="font-semibold text-slate-900 mb-2 line-clamp-2">{blog.title}</h2>
                        <p className="text-sm text-slate-500 line-clamp-3 mb-3">{blog.meta_description}</p>
                        <p className="text-xs text-slate-400">{blog.created_at ? new Date(blog.created_at).toLocaleDateString() : ''}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar Ads — Blog uses apartment instead of CreditCane */}
          <div className="hidden lg:block">
            <div className="sticky-sidebar space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl p-5 text-center">
                <p className="text-[10px] text-slate-400 mb-2">Advertisement</p>
                <div className="bg-slate-50 rounded-lg py-20 px-6 border border-dashed border-slate-200">
                  <p className="text-xs text-slate-400">Google AdSense</p>
                  <p className="text-[10px] text-slate-300">300x600</p>
                </div>
              </div>
              <a href="https://www.hellolanding.com/r/web/2vvg1H" target="_blank" rel="noopener" className="no-underline block">
                <div className="p-5 bg-gradient-to-br from-blue-50 via-sky-50 to-blue-100 rounded-xl border-2 border-blue-200 hover:shadow-md transition-all">
                  <img src="https://images.pexels.com/photos/19899060/pexels-photo-19899060.jpeg?auto=compress&cs=tinysrgb&w=260&h=140&fit=crop" alt="Furnished apartment" className="w-full h-24 rounded-lg object-cover mb-3" />
                  <p className="text-sm font-bold text-slate-900 mb-1">Get $250 Off Your First Apartment</p>
                  <p className="text-xs text-slate-500 mb-3">Move freely, stay comfortably. *Limited time offer.</p>
                  <span className="inline-block text-xs font-semibold text-white bg-blue-600 px-3 py-1.5 rounded-md">Claim $250 Off</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      <BottomAdBanner />
    </div>
  );
}
